<template>
  <div
    v-if="visible"
    ref="menu"
    class="dropdown-menu show position-fixed"
    :style="{ left: `${x}px`, top: `${y}px` }"
    @click.stop
  >
    <button class="dropdown-item d-flex align-items-center" @click="copyCoordinates">
      <span class="material-icons mr-2">my_location</span>
      {{ copyLabel }}
    </button>
    <button class="dropdown-item d-flex align-items-center" @click="handleCreateWaypoint">
      <span class="material-icons mr-2">add_location_alt</span>
      {{ $t('map.context_menu.create_waypoint') }}
    </button>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { OnBoardingGoal } from '../../../utils/models/OnBoardingGoal';
import { authReady } from '../../../utils/firebase/authReady';
import { i18n } from '../../../i18n';
import { formatGoToLocationLatLon } from '../../../utils/helpers/goToLocationLatLon';

export default {
  name: 'MapContextMenu',
  data() {
    return {
      visible: false,
      x: 0,
      y: 0,
      lngLat: null,
      copyLabel: i18n.global.t('map.context_menu.copy_coordinate'),
      hideCopyResetId: null
    };
  },
  mounted() {
    window.addEventListener('map-app-context-menu', this.handleMapContextMenu);
    document.addEventListener('click', this.hideMenu);
    document.addEventListener('contextmenu', this.handleDocumentContextMenu);
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('map-app-context-menu', this.handleMapContextMenu);
    document.removeEventListener('click', this.hideMenu);
    document.removeEventListener('contextmenu', this.handleDocumentContextMenu);
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.hideCopyResetId) {
      clearTimeout(this.hideCopyResetId);
      this.hideCopyResetId = null;
    }
  },
  methods: {
    ...mapActions('waypoints', { createWaypointAction: 'createWaypoint' }),

    handleMapContextMenu(event) {
      if (!event?.detail) return;
      this.showMenu(event.detail);
    },

    showMenu({ x, y, lngLat }) {
      const offset = 2;
      this.x = x + offset;
      this.y = y + offset;
      this.lngLat = lngLat;
      this.copyLabel = this.$t('map.context_menu.copy_coordinate');

      this.visible = true;

      this.$nextTick(() => {
        if (!this.$refs.menu) return;
        const rect = this.$refs.menu.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (rect.right > viewportWidth) {
          this.x = x - rect.width - offset;
        }

        if (rect.bottom > viewportHeight) {
          this.y = y - rect.height - offset;
        }
      });
    },

    hideMenu() {
      if (!this.visible) return;
      this.visible = false;
      if (this.hideCopyResetId) {
        clearTimeout(this.hideCopyResetId);
        this.hideCopyResetId = null;
      }
    },

    handleDocumentContextMenu(event) {
      if (!event.target.closest('.mapboxgl-canvas')) {
        this.hideMenu();
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.hideMenu();
      }
    },

    async copyCoordinates() {
      if (!this.lngLat) return;
      const lat = typeof this.lngLat.lat === 'function' ? this.lngLat.lat() : this.lngLat.lat;
      const lng = typeof this.lngLat.lng === 'function' ? this.lngLat.lng() : this.lngLat.lng;
      const text = formatGoToLocationLatLon(lat, lng);

      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
        } catch (fallbackError) {
          console.error('Failed to copy coordinates', fallbackError);
        }
        document.body.removeChild(textArea);
      }

      this.copyLabel = this.$t('common.copied');
      if (this.hideCopyResetId) {
        clearTimeout(this.hideCopyResetId);
      }
      this.hideCopyResetId = setTimeout(() => {
        this.copyLabel = this.$t('map.context_menu.copy_coordinate');
        this.hideCopyResetId = null;
      }, 2000);
    },

    async handleCreateWaypoint() {
      if (!this.lngLat) {
        this.hideMenu();
        return;
      }

      await authReady;
      if (!window.user) {
        window.onBoardingVue?.launchFor(OnBoardingGoal.Waypoints);
        this.hideMenu();
        return;
      }

      try {
        await this.createWaypointAction({ lngLat: this.lngLat });
      } catch (error) {
        console.error('Failed to create waypoint from context menu', error);
      }

      this.hideMenu();
    }
  }
};
</script>

<style scoped>
.dropdown-menu {
  position: fixed !important;
  margin: 0;
  min-width: 220px;
}
</style>
